import IconButton from '@/global/components/Button/IconButton'
import { cn } from '@/lib/utils'
import { LockIcon, PlusIcon, StickyNoteIcon } from 'lucide-react'
import { type ComponentProps, forwardRef, useId } from 'react'
import type { NotaAdesiva } from 'service/api'
import { match } from 'ts-pattern'
import { calcularEstadoIcone, type EstadoIconeNota } from './ancora'

export type NotaAdesivaIconProps = ComponentProps<typeof IconButton> & {
    /** Notas da âncora; vazio mostra o `+` tracejado. */
    notas: NotaAdesiva[]
}

/** Usa o total real de notas — o número do ícone para em 9. */
function montarRotuloAcessivel(tipo: EstadoIconeNota['tipo'], total: number) {
    if (tipo === 'vazio') return 'Adicionar nota'
    const isPlural = total > 1
    if (tipo === 'internas') {
        return `${total} ${isPlural ? 'notas internas' : 'nota interna'}`
    }
    return `${total} ${isPlural ? 'notas' : 'nota'}`
}

/**
 * Encaixe de um ícone Lucide dentro do viewBox 24 da nota. Tamanho vai como
 * atributo (`size`): `<svg>` aninhado ignora `width`/`height` de CSS, então
 * nem o `[&_svg:not([class*='size-'])]` do IconButton o afeta.
 */
function encaixar(x: number, y: number, tamanho: number) {
    return { x, y, size: tamanho }
}

/**
 * Nota com cadeado, `+` ou número, tudo num único SVG (viewBox 24) para os
 * encaixes manterem a mesma posição relativa em qualquer tamanho do ícone.
 */
const IconeNota = forwardRef<
    SVGSVGElement,
    ComponentProps<'svg'> & {
        lock?: boolean
        plus?: boolean
        number?: number
    }
>(({ lock, plus, number, className, ...rest }, ref) => {
    const idMascara = useId()

    return (
        <svg
            ref={ref}
            viewBox="0 0 24 24"
            fill="none"
            className={cn('size-5 overflow-visible', className)}
            {...rest}
        >
            {lock ? (
                // Recorta o contorno sob o cadeado, deixando o fundo real aparecer.
                <mask id={idMascara}>
                    <rect width={24} height={24} fill="white" />
                    <rect x={12} width={12} height={12} fill="black" />
                </mask>
            ) : null}
            <g mask={lock ? `url(#${idMascara})` : undefined}>
                {/* Espelhada na vertical: a dobra vai para baixo e libera o canto superior direito para o cadeado. */}
                <g transform="matrix(1 0 0 -1 0 24)">
                    <StickyNoteIcon {...encaixar(0, 0, 24)} />
                </g>
            </g>
            {lock ? (
                <LockIcon strokeWidth={4} {...encaixar(12, 0, 10.5)} />
            ) : null}
            {plus ? (
                <PlusIcon strokeWidth={4} {...encaixar(3.6, 3.6, 14.4)} />
            ) : null}
            {Number.isSafeInteger(number) ? (
                <text
                    x={6}
                    y={7.2}
                    dominantBaseline="hanging"
                    fontSize={10.8}
                    fontWeight="bold"
                    fill="currentColor"
                    className="select-none"
                >
                    {number}
                </text>
            ) : null}
        </svg>
    )
})

/**
 * Botão-ícone das notas de uma âncora da Digitação. Sem nota, só aparece no
 * hover/foco da linha (`tr`) ou com o popover aberto.
 */
export const NotaAdesivaIconButton = forwardRef<
    HTMLButtonElement,
    NotaAdesivaIconProps
>(({ notas, className, ...props }, ref) => {
    const estado = calcularEstadoIcone(notas)
    const isVazio = estado.tipo === 'vazio'

    return (
        <IconButton
            ref={ref}
            aria-label={montarRotuloAcessivel(estado.tipo, notas.length)}
            data-tipo={estado.tipo}
            className={cn(
                'relative inline-flex size-6 shrink-0 cursor-pointer items-center justify-center',
                'transition-[opacity,scale] hover:text-foreground',
                'focus-visible:outline-2 focus-visible:outline-ring',
                // Cresce para a esquerda: à direita fica a borda da célula, que corta o overflow.
                'hover:scale-150 origin-right',
                isVazio && [
                    'opacity-0 focus-visible:opacity-100 data-[state=open]:opacity-100',
                    '[tr:hover_&]:opacity-100 [tr:focus-within_&]:opacity-100',
                ],
                !isVazio && 'text-primary hover:opacity-70',
                className,
            )}
            title="Adicionar nota"
            {...props}
        >
            {isVazio ? (
                <IconeNota className="opacity-70" aria-hidden plus />
            ) : (
                <IconeNota
                    lock={estado.tipo === 'internas'}
                    number={match(estado.quantidade)
                        .when(
                            x => x < 1,
                            () => undefined,
                        )
                        .when(
                            x => x > 9,
                            () => 9,
                        )
                        .otherwise(x => x)}
                />
            )}
        </IconButton>
    )
})
NotaAdesivaIconButton.displayName = 'NotaAdesivaIcon'
